import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}

function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}

function PurpleButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    />
  );
}

export default function PPMAssetsList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [assets, setAssets] = useState([]);
  const [qText, setQText] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      // If some docs don't have name/code, orderBy can fail.
      // If that's a risk, remove orderBy and sort client-side.
      const snap = await getDocs(query(collection(db, "ppmassets"), orderBy("code")));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAssets(list);
    } catch (e) {
      // fallback: try without orderBy (in case of missing field)
      try {
        const snap = await getDocs(collection(db, "ppmassets"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        setAssets(list);
      } catch (ex) {
        setErr(ex?.message || e?.message || "Failed to load machines.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = qText.trim().toLowerCase();
    if (!s) return assets;

    return assets.filter((a) => {
      const hay = [
        a.name,
        a.code,
        a.location,
        a.department,
        a.serialNumber,
        a.vendor,
        a.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(s);
    });
  }, [assets, qText]);

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">PPM Machines</h1>
          <p className="text-gray-600 mt-1">
            Click a machine to edit it. Creates/edits live in <span className="font-semibold">PPMAssetCreate</span>.
          </p>
        </div>

        <div className="flex gap-2">
          <GhostButton onClick={load} disabled={loading}>
            Refresh
          </GhostButton>
          <PurpleButton onClick={() => navigate("/PPMAssetCreate")}>
            + New Machine
          </PurpleButton>
        </div>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-lg font-extrabold text-gray-900">
              Machines ({filtered.length})
            </div>
            <div className="text-sm text-gray-600">
              Search by name, code, location, department, serial, vendor, or id.
            </div>
          </div>

          <div className="w-full md:w-[320px]">
            <Input
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Search…"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-gray-600">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-gray-600">No machines found.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((a) => {
              const active = a.active !== false;
              const taskCount = Array.isArray(a.tasks) ? a.tasks.length : 0;

              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/PPMAssetCreate/${a.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") navigate(`/PPMAssetCreate/${a.id}`);
                  }}
                  className={cn(
                    "px-4 py-3 cursor-pointer select-none hover:bg-gray-50",
                    !active && "opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-extrabold text-gray-900">
                        {a.name || "Unnamed machine"}
                        {!active ? (
                          <span className="ml-2 text-xs font-semibold text-red-700">INACTIVE</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {a.code ? `Code: ${a.code}` : `ID: ${a.id}`}
                        {a.location ? ` · ${a.location}` : ""}
                        {a.department ? ` · ${a.department}` : ""}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Tasks: <span className="font-semibold">{taskCount}</span>
                      </div>
                    </div>

                    <div className="text-sm font-semibold text-purple-700">
                      Edit →
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
